import { useRef } from 'react';
import { captureException, captureMessage } from '@sentry/react';
import { useHistory } from 'react-router-dom';
import {
  ApplePay,
  Card,
  CashAppPay,
  GooglePay,
  Payments,
  TokenResult,
} from '@square/web-payments-sdk-types';
import { trackCdp } from 'utilities/tracking';
import { PAYMENT_RESPONSE_ERRORS } from 'typescript/constants/mappings';
import { usePaymentContext } from 'typescript/context/payment_context';
import { useLogCompleteOrderAction } from 'typescript/apps/checkout_v3/hooks/use_log_complete_order_action';
import { useSavedCardsContext } from 'typescript/context/saved_cards';
import {
  createShopPaymentWithSavedCard,
  createShopPaymentWithToken,
  checkPreAuthFraud,
  getPaymentDataValidationError,
} from 'typescript/utilities/shop_payments';
import {
  useCompleteOrder,
  useGetOrderSummary,
  useUpdateOrderAddressInformation,
} from 'typescript/hooks/use_order_actions_client';
import {
  PaymentSchedule,
  PaymentType,
  PreAuthFraudDecision,
} from 'typescript/constants/enums/payment';
import {
  CHECKOUT_ROUTES,
  CompleteOrderError,
} from 'typescript/constants/enums/checkout';
import {
  useCompleteOrderContext,
  type PendingCompleteOrder,
} from 'typescript/context/complete_order_context';
import {
  buildBillingAddressFromBillingContact,
  removePrefixFromAddress,
} from 'typescript/utilities/address_utils';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import { useSubscriptionDisclosure } from 'typescript/apps/checkout_v3/hooks/use_subscription_disclosure';
import CDP_EVENT_NAMES from 'typescript/constants/cdp_events';
import { COMPONENT_NAMES, FEATURES } from 'typescript/constants/tracking';
import { BILLING_PREFIX } from 'typescript/schemas/address_forms/address_fields';
import { OrderAddress } from 'typescript/types/api/solidshop';
import {
  CheckoutErrorCode,
  NormalizedCheckoutError,
  boundedDetailValue,
  normalizeCheckoutError,
  normalizeCheckoutResponseError,
  normalizeCompleteOrderError,
} from 'typescript/utilities/checkout_error_telemetry';

export type PaymentMethod = Card | ApplePay | CashAppPay | GooglePay;

// Codes the server returns when charging the payment fails during completion.
const PAYMENT_COMPLETION_ERROR_CODES: ReadonlySet<CheckoutErrorCode> = new Set([
  'payment_authorization_failed',
  'payment_customer_creation_failed',
  'payment_card_creation_failed',
  'issuer_installment_failed',
  'instrument_verification_failed',
  'card_verification_required',
  'invalid_card_data',
  'card_source_already_used',
  'card_source_expired',
  'seller_card_save_failed',
  'card_migration_failed',
]);

// Payment completion codes with their own copy; the rest get the payment copy.
const PAYMENT_COMPLETION_ERRORS: Partial<
  Record<CheckoutErrorCode, CompleteOrderError>
> = {
  card_verification_required: CompleteOrderError.CARD_VERIFICATION_REQUIRED,
  card_source_already_used: CompleteOrderError.CARD_SOURCE_UNUSABLE,
  card_source_expired: CompleteOrderError.CARD_SOURCE_UNUSABLE,
  seller_card_save_failed: CompleteOrderError.CARD_SAVE_FAILED,
};

// A saved card the buyer doesn't own gets its own copy; every other payment
// creation failure gets the generic copy.
const createPaymentError = ({ errorCode }: NormalizedCheckoutError) =>
  errorCode === 'payment_source_not_owned'
    ? CompleteOrderError.SAVED_CARD_NOT_OWNED
    : CompleteOrderError.GENERIC;

// The card form already shows inline errors for its own validation; wallets
// and Cash App have no inline errors to fall back on.
const tokenFailureError = (
  sdkStatus: string | undefined,
  paymentType: PaymentType,
) =>
  sdkStatus === 'Invalid' && paymentType === PaymentType.CARD
    ? undefined
    : CompleteOrderError.GENERIC;

const useSubmitOrder = (orderNumber: string) => {
  const paymentPostalCodeRef = useRef('');

  const history = useHistory();
  const { trackingData } = useCheckoutFlow();
  const {
    setCompleteOrderError,
    setShowCompletingOrderDialog,
    setShowRecaptcha,
    setPendingCompleteOrder,
    takePendingCompleteOrder,
  } = useCompleteOrderContext();
  const {
    setSubmittingPayment,
    subscriptionConsentGiven,
    setSubscriptionConsentError,
  } = usePaymentContext();
  const { consentRequired: subscriptionConsentRequired } =
    useSubscriptionDisclosure();
  const {
    orderSummaryData,
    isPending: orderDataIsPending,
    isError: orderDataIsError,
  } = useGetOrderSummary({ orderNumber });
  const {
    payWithSavedCard,
    setPayWithSavedCard,
    makeDefaultCard,
    saveCardToWallet,
    selectedCard,
  } = useSavedCardsContext();
  const { completeOrder } = useCompleteOrder({ orderNumber });
  const { updateOrderAddressInformationAsync } =
    useUpdateOrderAddressInformation({
      orderNumber,
    });
  const { logCompleteOrderAction } = useLogCompleteOrderAction({
    orderNumber,
    orderSummaryData,
  });

  const paymentSchedule = orderSummaryData?.payment_due
    ?.payment_schedule as PaymentSchedule;

  const tokenize = async ({
    paymentMethod,
    paymentType,
    cashAppTokenResult = undefined,
  }: {
    paymentMethod: PaymentMethod;
    paymentType: PaymentType;
    cashAppTokenResult?: TokenResult;
  }) => {
    let tokenResult: TokenResult | undefined;

    if (cashAppTokenResult && paymentType === PaymentType.CASH_APP_PAY) {
      tokenResult = cashAppTokenResult;
    } else if (
      !selectedCard ||
      paymentType === PaymentType.GOOGLE_PAY ||
      paymentType === PaymentType.APPLE_PAY
    ) {
      tokenResult = await (
        paymentMethod as Card | ApplePay | GooglePay
      ).tokenize();
    }

    return tokenResult;
  };

  const getPreAuthFraudDecision = async ({
    paymentType,
    tokenResult,
  }: {
    paymentType: PaymentType;
    tokenResult?: TokenResult;
  }): Promise<{
    decision?: PreAuthFraudDecision;
    error?: NormalizedCheckoutError;
  }> => {
    // Don't check pre-auth fraud if cash app or saved card
    if (
      paymentType !== PaymentType.CASH_APP_PAY &&
      !(paymentType === PaymentType.CARD && selectedCard)
    ) {
      // Fail closed: a fraud check without a usable decision blocks checkout.
      let response: Response | null;
      try {
        response = await checkPreAuthFraud(
          orderNumber,
          tokenResult as TokenResult,
        );
      } catch (error) {
        return { error: normalizeCheckoutError({ error }) };
      }

      // null means building the request threw, not that the network failed.
      if (!response) {
        return { error: { errorCode: 'client_error' } };
      }

      if (!response.ok) {
        return {
          error: await normalizeCheckoutResponseError({
            response,
            httpErrorCode: 'fraud_check_failed',
          }),
        };
      }

      const responseData = await response.json().catch(() => undefined);
      const decision = responseData?.decision;
      if (!Object.values(PreAuthFraudDecision).includes(decision)) {
        // JSON has no undefined, so an undefined body means it didn't parse.
        // The backend may pass Forter's raw action through, so it is bounded.
        const detail =
          responseData === undefined
            ? 'invalid_json'
            : `decision: ${boundedDetailValue(decision)}`;
        return {
          error: {
            errorCode: 'fraud_check_failed',
            errorDetails: `http_status: ${response.status}, ${detail}`,
          },
        };
      }

      return { decision };
    }

    return { decision: PreAuthFraudDecision.APPROVE };
  };

  const createShopPayment = async ({
    paymentType,
    tokenResult,
    sqPayments = undefined,
    billingAddress = undefined,
  }: {
    paymentType: PaymentType;
    tokenResult?: TokenResult;
    sqPayments?: Payments;
    billingAddress?: OrderAddress;
  }): Promise<{ success: boolean; error?: NormalizedCheckoutError }> => {
    if (selectedCard && paymentType === PaymentType.CARD) {
      setShowCompletingOrderDialog(true);

      try {
        const response = await createShopPaymentWithSavedCard(
          orderNumber,
          selectedCard,
        );
        if (response.ok) {
          setPayWithSavedCard(true);
          return { success: true };
        }

        const error = await normalizeCheckoutResponseError({ response });
        setCompleteOrderError(createPaymentError(error));
        return { success: false, error };
      } catch (error) {
        setCompleteOrderError(CompleteOrderError.GENERIC);
        return { success: false, error: normalizeCheckoutError({ error }) };
      }
    } else if (tokenResult && tokenResult.status === 'OK') {
      setShowCompletingOrderDialog(true);

      paymentPostalCodeRef.current =
        tokenResult.details?.billing?.postalCode || '';

      // This should never happen since orderData should be fetched/cached before submission
      if (orderDataIsPending || orderDataIsError) {
        captureMessage('Order data is not available for payment submission', {
          extra: {
            isError: orderDataIsError,
            isPending: orderDataIsPending,
          },
        });

        setCompleteOrderError(CompleteOrderError.GENERIC);
        return {
          success: false,
          // The query error carries no status or network tag to classify it
          // by. Pending means the buyer submitted before checkout had data.
          error: orderDataIsError
            ? {
                errorCode: 'unknown_error',
                errorDetails: 'order_data_unavailable',
              }
            : { errorCode: 'client_error', errorDetails: 'order_data_pending' },
        };
      }

      try {
        const response = await createShopPaymentWithToken(
          orderNumber,
          orderSummaryData,
          tokenResult,
          sqPayments,
          null,
          saveCardToWallet,
          billingAddress,
        );
        if (response.ok) {
          setPayWithSavedCard(false);
          return { success: true };
        }

        const error = await normalizeCheckoutResponseError({ response });
        setCompleteOrderError(createPaymentError(error));
        return { success: false, error };
      } catch (error) {
        setCompleteOrderError(CompleteOrderError.GENERIC);
        return { success: false, error: normalizeCheckoutError({ error }) };
      }
    }

    // submitOrder validates the token first, so this is a fallback only.
    setCompleteOrderError(CompleteOrderError.GENERIC);
    return {
      success: false,
      error: {
        errorCode: 'client_error',
        errorDetails: 'unexpected_payment_state',
      },
    };
  };

  const updateBillingAddress = async ({
    billingAddress,
  }: {
    billingAddress?: Record<string, string | boolean>;
  }) => {
    const address = { ...billingAddress, billing_address_only: true };

    // Failures here should generally not happen, though can depend on data
    // returned from Web Payment SDK tokenize(). Do not block order submission
    // on billing address update failure and just log the error.
    try {
      await updateOrderAddressInformationAsync({ address });
    } catch (error) {
      captureException(error);
    }
  };

  const completeOrderWithRecaptcha = (
    recaptchaResponse: string,
    {
      paymentType,
      paymentSchedule,
      paymentPostalCode,
      useSavedPaymentSource,
      makeDefaultCard,
      financingDisclosureAcknowledged,
    }: PendingCompleteOrder,
  ) => {
    setShowRecaptcha(false);
    setSubmittingPayment(true);
    setShowCompletingOrderDialog(true);

    completeOrder(
      {
        recaptchaResponse,
        paymentPostalCode,
        useSavedPaymentSource,
        makeDefaultCard,
        financingDisclosureAcknowledged,
      },
      {
        onSuccess: (response) => {
          logCompleteOrderAction({
            status: 'success',
            paymentType,
            paymentSchedule,
            telemetry: response?.telemetry,
          });
          // set the state upon success to hide the completing order dialog
          setSubmittingPayment(false);
          setShowCompletingOrderDialog(false);
        },
        onError: (error) => {
          const errorName = error?.name;
          const errorMessage = error?.message;
          const errorDetails = errorMessage
            ? `${errorName}: ${errorMessage}`
            : errorName;
          const normalizedError = normalizeCompleteOrderError(error);

          let cdpEventName;

          // if it is a payment error, set the payment submission error.
          // "Payment required" and "Card details required" share
          // order_validation_failed with non-payment failures, so the message
          // identifies them.
          if (
            PAYMENT_COMPLETION_ERROR_CODES.has(normalizedError.errorCode) ||
            Object.values(PAYMENT_RESPONSE_ERRORS).includes(errorMessage)
          ) {
            cdpEventName =
              CDP_EVENT_NAMES.CHECKOUT_V3.PAYMENT
                .COMPLETING_ORDER_ERROR_PAYMENT;
            setCompleteOrderError(
              PAYMENT_COMPLETION_ERRORS[normalizedError.errorCode] ??
                CompleteOrderError.PAYMENT,
            );
          } else {
            cdpEventName =
              CDP_EVENT_NAMES.CHECKOUT_V3.PAYMENT
                .COMPLETING_ORDER_ERROR_GENERAL;
            // if it isn't a payment error, then it's some order completion error which we show a generic message for
            setCompleteOrderError(CompleteOrderError.GENERIC);
          }

          trackCdp({
            name: cdpEventName,
            params: {
              feature: FEATURES.SHOP.NAME,
              step: COMPONENT_NAMES.APPS.CHECKOUT_V3.PAYMENT,
              error: errorDetails,
              error_code: normalizedError.errorCode,
              ...trackingData,
            },
          });

          logCompleteOrderAction({
            status: 'error',
            paymentType,
            paymentSchedule,
            errorStage: 'complete_order',
            ...normalizedError,
          });

          // reset the submitting payment state so they can try again
          setSubmittingPayment(false);
          setShowCompletingOrderDialog(false);
        },
        onSettled: (_data, error) => {
          // if there are no payment errors, redirect to the complete page
          if (!error) {
            history.push(CHECKOUT_ROUTES.COMPLETE);
          }
        },
      },
    );
  };

  const completeOrderAfterRecaptcha = (recaptchaResponse: string) => {
    const pendingCompleteOrder = takePendingCompleteOrder();
    if (!pendingCompleteOrder) {
      return;
    }

    completeOrderWithRecaptcha(recaptchaResponse, pendingCompleteOrder);
  };

  const cancelPendingCompleteOrder = () => {
    setPendingCompleteOrder(null);
  };

  const submitOrder = async ({
    paymentMethod,
    paymentType,
    billingAddress = undefined,
    sqPayments = undefined,
    cashAppTokenResult = undefined,
    financingDisclosureAcknowledged = false,
    subscriptionConsentPreVerified = false,
  }: {
    paymentMethod: PaymentMethod;
    paymentType: PaymentType;
    billingAddress?: Record<string, string | boolean>;
    sqPayments?: Payments;
    cashAppTokenResult?: TokenResult;
    financingDisclosureAcknowledged?: boolean;
    // Set on the mobile Cash App Pay redirect-return path only: consent was
    // already enforced before the redirect launched (the Cash App button is not
    // attached until the checkbox is checked), but the return lands on a fresh
    // page where the in-memory consent state is gone. Without this, the gate
    // below would re-block and strand the buyer on a spinner.
    subscriptionConsentPreVerified?: boolean;
  }) => {
    // Legal gate: opt_out carts require affirmative subscription consent.
    if (
      subscriptionConsentRequired &&
      !subscriptionConsentGiven &&
      !subscriptionConsentPreVerified
    ) {
      setSubscriptionConsentError(true);
      return;
    }

    setSubmittingPayment(true);

    logCompleteOrderAction({
      status: 'initiated',
      paymentType,
      paymentSchedule,
    });

    // Ends this attempt before order completion so the buyer can retry. Error
    // copy is omitted when the buyer cancelled or the SDK shows inline errors.
    const stopSubmit = (
      action: Partial<Parameters<typeof logCompleteOrderAction>[0]>,
      completeOrderError?: CompleteOrderError,
    ) => {
      logCompleteOrderAction({
        status: 'error',
        paymentType,
        paymentSchedule,
        ...action,
      });
      if (completeOrderError) {
        setCompleteOrderError(completeOrderError);
      }
      setSubmittingPayment(false);
      setShowCompletingOrderDialog(false);
    };

    let tokenResult: TokenResult | undefined;
    try {
      tokenResult = await tokenize({
        paymentMethod,
        paymentType,
        cashAppTokenResult,
      });
    } catch (error) {
      // Log the error name only; SDK messages are free text.
      const errorName = error instanceof Error ? error.name : undefined;
      stopSubmit(
        {
          errorStage: 'tokenize',
          errorCode: 'tokenization_failed',
          errorDetails: `error_name: ${boundedDetailValue(errorName, 'unknown')}`,
        },
        CompleteOrderError.GENERIC,
      );
      return;
    }

    // Saved cards are charged by ID; every other path needs a usable token.
    if (!(selectedCard && paymentType === PaymentType.CARD)) {
      const sdkStatus = tokenResult?.status;
      const sdkStatusDetails = sdkStatus
        ? { errorDetails: `sdk_status: ${sdkStatus}` }
        : {};

      if (sdkStatus === 'Cancel' || sdkStatus === 'Abort') {
        // The buyer closed the wallet or Cash App sheet.
        stopSubmit({
          status: 'cancelled',
          errorStage: 'tokenize',
          ...sdkStatusDetails,
        });
        return;
      }

      if (sdkStatus !== 'OK') {
        stopSubmit(
          {
            errorStage: 'tokenize',
            errorCode: 'tokenization_failed',
            ...sdkStatusDetails,
          },
          tokenFailureError(sdkStatus, paymentType),
        );
        return;
      }

      const paymentDataError = getPaymentDataValidationError(tokenResult);
      if (paymentDataError) {
        stopSubmit(
          {
            errorStage: 'tokenize',
            errorCode: 'tokenization_failed',
            errorDetails: `payload_validation: ${paymentDataError}`,
          },
          CompleteOrderError.GENERIC,
        );
        return;
      }
    }

    const preAuthFraud = await getPreAuthFraudDecision({
      paymentType,
      tokenResult,
    });
    if (preAuthFraud.error) {
      // A failed fraud check still blocks checkout, but only a real fraud
      // decision gets the fraud-decline copy.
      stopSubmit(
        { errorStage: 'pre_auth_fraud', ...preAuthFraud.error },
        CompleteOrderError.GENERIC,
      );
      return;
    }
    if (preAuthFraud.decision !== PreAuthFraudDecision.APPROVE) {
      stopSubmit(
        {
          errorStage: 'pre_auth_fraud',
          errorCode: 'fraud_declined',
          errorDetails: `decision: ${preAuthFraud.decision}`,
        },
        CompleteOrderError.PRE_AUTH,
      );
      return;
    }

    const billingAddressWithPrefix =
      paymentType === PaymentType.CARD
        ? billingAddress
        : buildBillingAddressFromBillingContact({
            billingContact: tokenResult?.details?.billing,
          });

    const createShopPaymentResult = await createShopPayment({
      paymentType,
      tokenResult,
      sqPayments,
      billingAddress: removePrefixFromAddress(
        billingAddressWithPrefix,
        BILLING_PREFIX,
      ),
    });

    if (createShopPaymentResult.success) {
      await updateBillingAddress({
        billingAddress: billingAddressWithPrefix,
      });

      const pendingCompleteOrder = {
        paymentType,
        paymentSchedule,
        paymentPostalCode: paymentPostalCodeRef.current,
        useSavedPaymentSource:
          paymentType === PaymentType.CARD && payWithSavedCard,
        makeDefaultCard: paymentType === PaymentType.CARD && makeDefaultCard,
        financingDisclosureAcknowledged,
      };

      if (orderSummaryData?.recaptcha_required) {
        setPendingCompleteOrder(pendingCompleteOrder);
        setSubmittingPayment(false);
        setShowCompletingOrderDialog(false);
        setShowRecaptcha(true);
        return;
      }

      completeOrderWithRecaptcha(false.toString(), pendingCompleteOrder);
    } else {
      stopSubmit({
        errorStage: 'create_payment',
        ...createShopPaymentResult.error,
      });
    }
  };

  return {
    submitOrder,
    completeOrderAfterRecaptcha,
    cancelPendingCompleteOrder,
  };
};

export { useSubmitOrder };
